import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classnames from 'classnames';
import {Scrollbars} from 'react-custom-scrollbars';
import OnClickOutside from 'react-onclickoutside';


import styles from './SearchTextField.scss';
import {TextField, Spinner} from 'components/controls';

class SearchTextField extends PureComponent {
    static propTypes = {
        light: PropTypes.bool,
        placeholder: PropTypes.string,
        searchResult: PropTypes.array,
        searchResultProcessing: PropTypes.bool,
        onSearch: PropTypes.func,
        onChoose: PropTypes.func.isRequired,
        autoClear: PropTypes.bool,
        needFullResult: PropTypes.bool,
        error: PropTypes.string,
        label: PropTypes.string,
        bodyClassName: PropTypes.string,
        inputClassName: PropTypes.string,
        inputContainerClassName: PropTypes.string,
        autoHeightMax: PropTypes.bool,
        itemClassName: PropTypes.string,
        notFoundLabel: PropTypes.string,
        formatter: PropTypes.func,
        withoutDebouncing: PropTypes.bool,
        onChange: PropTypes.func.isRequired,
        withoutSearch: PropTypes.bool,
        alwaysShow: PropTypes.bool,
        hardSet: PropTypes.bool,
        disabled: PropTypes.bool,
        minValueLengthToSearch: PropTypes.number,
        loadOnScroll: PropTypes.func,
        totalCount: PropTypes.number
    };

    static defaultProps = {
        error: '',
        label: '',
        light: false,
        autoClear: true,
        hardSet: true,
        placeholder: '',
        searchResult: [],
        searchResultProcessing: false,
        inputClassName: null,
        bodyClassName: null,
        inputContainerClassName: null,
        needFullResult: false,
        notFoundLabel: loc('no-results-found'),
        formatter: item => item.name,
        withoutDebouncing: false,
        disabled: false,
        minValueLengthToSearch: 1,
    };

    state = {
        value: '',
        isFocused: false,
        processing: false,
    };

    constructor(props) {
        super(props);

        if (props.value && !props.hardSet) {
            this.state = {
                value: props.value.name || props.value,
            }
        }

        this.scrollbarRef = React.createRef();
    }

    componentWillReceiveProps(next) {
        if (!next.value && this.props.value) {
            this.setState({value: ''});
        }
        if (next.searchResult.length !== this.props.searchResult.length) {
            const {scrollTop} = this.scrollbarRef.current.getValues();
            this.setState({scrollTop, isScrollBlocked: true})
        }
    }

    debouncedSearch = _.debounce(:: this.search, 500);

    render() {
        const {light, placeholder, searchResultProcessing, inputClassName, inputContainerClassName, error, disabled} = this.props;
        const {value} = this.state;
        const loaderClassName = classnames(styles.loader, searchResultProcessing ? styles.visible : '');
        const containerClassname = classnames(styles.container, {[styles.white]: light});
        const inputStyles = classnames(
            styles.input,
            inputClassName,
            {[styles.error]: Boolean(error)}
        );
        const inputContainerStyle = classnames(styles.fieldContainer, inputContainerClassName, {[styles.error]: Boolean(error), [styles.disabled]: disabled});

        return (
            <div className={containerClassname} onClick={this.changeFocus.bind(this, true)}>
                <div className={inputContainerStyle}>
                    <TextField
                        value={value}
                        className={inputStyles}
                        placeholder={placeholder}
                        onChange={::this.handleChange}
                        onFocus={::this.handleFocus}
                        disabled={disabled}
                    />
                    <div className={loaderClassName}>
                        <Spinner size={10} />
                    </div>
                </div>
                {this.renderResults()}
            </div>
        )
    }

    handleChange(e) {
        const {value} = e.target;
        const {withoutDebouncing, onChange, hardSet, withoutSearch} = this.props;
        const searchFn = withoutDebouncing ? this.search : this.debouncedSearch;

        this.setState({value, processing: !withoutDebouncing}, () => {
            if (onChange && !hardSet) {
                onChange({target: {value}});
            }
            if (!withoutSearch) {
                searchFn();
            }
        });
    }

    handleFocus() {
        const {autoFetch} = this.props;
        if (autoFetch) {
            this.search();
        }

        this.changeFocus(true);
    }

    changeFocus(isFocused) {
        this.setState({isFocused});
    }

    search() {
        const {onSearch, filter, minValueLengthToSearch} = this.props;
        const {value} = this.state;

        if (value.length < minValueLengthToSearch) {
            return;
        }

        this.setState({processing: false}, onSearch.bind(this, value, filter));
    }

    onScrollStop = () => {
        const {loadOnScroll, searchResult, totalCount} = this.props;
        if (!loadOnScroll || !(totalCount > searchResult.length)) return;

        const {value, isScrollBlocked} = this.state;
        const scrollbar = this.scrollbarRef.current;

        if (isScrollBlocked) {
            return this.setState({isScrollBlocked: false}, () => scrollbar.scrollTop(this.state.scrollTop))
        }

        const {top, scrollTop} = scrollbar.getValues();
        if (top === 1) {
            this.setState({scrollTop, processing: false}, () => {
                loadOnScroll(value, searchResult.length);
            });
        }
    }

    renderResults() {
        const {
            searchResult,
            needFullResult,
            searchResultProcessing,
            bodyClassName,
            autoHeightMax,
            itemClassName,
            notFoundLabel,
            alwaysShow,
            formatter,
            minValueLengthToSearch,
        } = this.props;
        const {isFocused, value, processing} = this.state;

        const isEmpty = (!value.length && !alwaysShow) && isFocused;
        const notFound = !searchResult.length && value.length >= minValueLengthToSearch && !searchResultProcessing;
        const needToShowContainer = isFocused && !processing && (!isEmpty || notFound);

        const containerClassName = classnames(
            styles.resultsContainer,
            bodyClassName,
            {[styles.nonEmpty]: needToShowContainer}
        );

        const itemStyleName = classnames(styles.item, itemClassName);

        return (
            <div className={containerClassName}>
                <Scrollbars
                    onScrollStop={this.onScrollStop}
                    autoHeightMax={autoHeightMax ? autoHeightMax : '15rem'}
                    ref={this.scrollbarRef}
                    autoHeight
                >
                    {notFound &&
                        <div key='not-found' className={itemStyleName}>
                            {notFoundLabel}
                        </div>
                    }
                    {searchResult.map((result, index) => {
                        const finalResult = needFullResult ? result : result.name;
                        return (
                            <div key={result.id || index} className={itemStyleName}
                                onClick={this.handleChoose.bind(this, finalResult)}>
                                {formatter(result)}
                            </div>
                        );
                    })}
                </Scrollbars>
            </div>
        );
    }

    handleChoose(value) {
        const {autoClear, onChoose} = this.props;
        onChoose(value);

        this.setState({value: autoClear ? '' : this.props.formatter(value)}, () => {
            this.changeFocus(false);
        });
    }

    handleClickOutside = () => {
        this.changeFocus(false);
    };
}

export default OnClickOutside(SearchTextField);



// WEBPACK FOOTER //
// ./src/components/controls/SearchTextField.jsx